import { useCallback, useEffect, useMemo, useState } from 'react';
import { LabeledContainer, Button } from '@schibsted-svp/react-ui';
import { useFormikContext } from 'formik';
import classNames from 'classnames';
import get from 'lodash/get';

import { uploadImage } from '../../../services/api/images';
import { blobToDataUrl, loadImage } from '../../../utils';
import { withFetchStatus } from '../../../services/notifier';

import { ImageDropzone } from './ImageDropzone/ImageDropzone';
import { ImageCropper } from './ImageCropper/ImageCropper';

import type { FC } from 'react';

import styles from './ImageUploadInput.module.scss';

type Props = {
  label?: string;
  name: string;
  aspectRatio?: number;
  displayImageUrl?: boolean;
};

const ImageUploadInput: FC<Props> = ({
  label,
  name,
  aspectRatio = 16 / 9,
  displayImageUrl = false,
}) => {
  const { values, errors, setFieldValue } = useFormikContext();
  const fieldErrors = errors as Record<string, unknown>;
  const [droppedImageDataUrl, setDroppedImageDataUrl] = useState<string | null>(
    null
  );
  const [uploadedImage, setUploadedImage] = useState(get(values, name));
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const invertedAspectRatioPadding = useMemo(
    () => `${(1 / aspectRatio) * 100}%`,
    [aspectRatio]
  );

  const hasUploadedImage = Boolean(uploadedImage);
  const imageUrl = useMemo(
    () => (uploadedImage ? `${uploadedImage}?t[]=580q80` : null),
    [uploadedImage]
  );

  const hasError = typeof fieldErrors[name] === 'string';

  const showImageUrl = displayImageUrl && imageUrl;

  const preloadImage = useCallback(async (imageUrl: string) => {
    await loadImage(imageUrl);
    setIsImageLoaded(true);
  }, []);

  useEffect(() => {
    if (imageUrl) {
      preloadImage(imageUrl);
    }
  }, [imageUrl, preloadImage]);

  function onCropperClose() {
    setDroppedImageDataUrl(null);
  }

  function onRemoveImage() {
    setFieldValue(name, null);
    setUploadedImage(null);
    setDroppedImageDataUrl(null);
  }

  return (
    <LabeledContainer label={label}>
      <div className={styles.container}>
        <div className={styles.imagePreview}>
          {hasUploadedImage && isImageLoaded ? (
            <img src={imageUrl ?? undefined} alt="" />
          ) : (
            <div
              className={styles.aspectRatio}
              style={{ paddingBottom: invertedAspectRatioPadding }}
            />
          )}
        </div>
        <ImageDropzone
          className={classNames(styles.dropzone, {
            [styles.hasError]: hasError,
          })}
          onDrop={async (image) => {
            const imageDataUrl = await blobToDataUrl(image);
            setDroppedImageDataUrl(imageDataUrl as string);
          }}
        />
      </div>
      {hasUploadedImage && isImageLoaded && (
        <Button
          type="button"
          onClick={onRemoveImage}
          variant="standard"
          size="small"
          className={styles.removeButton}
        >
          Remove image
        </Button>
      )}
      <ImageCropper
        onClose={onCropperClose}
        imageDataUrl={droppedImageDataUrl}
        aspectRatio={aspectRatio}
        buttonLabel="Upload"
        onCrop={async (imageBlob) => {
          const url = await withFetchStatus(uploadImage, {
            success: 'Image uploaded',
            error: 'Failed to upload image',
          })(imageBlob as File);

          setFieldValue(name, url);
          setDroppedImageDataUrl(null);
          setUploadedImage(url);
        }}
      />
      {hasError && (
        <p className={styles.error}>{fieldErrors[name] as string}</p>
      )}
      {showImageUrl && (
        <p>
          <b>Image url:</b>
          {imageUrl}
        </p>
      )}
    </LabeledContainer>
  );
};

export { ImageUploadInput };
